import type { CSSProperties } from 'react';

/**
 * Jetons MAAT (charte groupe-maat.com, SYNTHESE §2). L'orange #E5521F est décoratif uniquement :
 * aucun texte ne s'écrit avec, aucun fond de texte non plus. Les boutons utilisent #C2410C.
 * Le gris-bleu clair des anciennes maquettes est interdit pour du texte courant (SYNTHESE §3, point 5) : il n’apparaît nulle part.
 */
export type Variante = 'equipe' | 'client';

export const COULEURS = {
  marine: '#383D6C',
  orange: '#E5521F',
  bouton: '#C2410C',
  corail: '#EE8B77',
  corailClair: '#F2A9A5',
  texte: '#1F2347',
  texteSecondaire: '#5D6080',
  fond: '#FFFFFF',
  fondDoux: '#F4F4F8',
  bordure: '#6B7094',
  /** Filets décoratifs (cartes, séparateurs de lignes) : repris du prototype de démo ; jamais seuls pour délimiter un champ ou un bouton. */
  bordureDouce: '#E2E1EA',
  bordureForte: '#C9CBE0',
  focus: '#383D6C',
  erreur: '#9B1C1C',
  fondErreur: '#FDECEC',
} as const;

export interface CouleurStatut {
  texte: string;
  fond: string;
}

/** « Hypothèse » et « à vérifier » sont toujours distincts de « vérifié » (UX-30). */
export const COULEURS_STATUT = {
  verifie: { texte: '#14532D', fond: '#DCFCE7' },
  hypothese: { texte: '#5B21B6', fond: '#EDE9FE' },
  a_verifier: { texte: '#78350F', fond: '#FEF3C7' },
  en_cours: { texte: '#1E3A8A', fond: '#DBEAFE' },
  termine: { texte: '#FFFFFF', fond: '#166534' },
  bloque: { texte: '#7F1D1D', fond: '#FEE2E2' },
  en_attente: { texte: '#374151', fond: '#E5E7EB' },
  equipe_maat: { texte: '#FFFFFF', fond: COULEURS.marine },
  masquee_famille: { texte: COULEURS.texte, fond: COULEURS.corailClair },
} as const satisfies Record<string, CouleurStatut>;

export type StatutBadge = keyof typeof COULEURS_STATUT;

export interface PaireTexte {
  nom: string;
  texte: string;
  fond: string;
  /** Grand texte (au moins 24 px, ou 19 px gras) : seuil de 3:1. */
  grand?: boolean;
}

/** Toutes les paires texte et fond employées par le kit, vérifiées une à une par les tests. */
export const PAIRES_TEXTE: readonly PaireTexte[] = [
  { nom: 'texte', texte: COULEURS.texte, fond: COULEURS.fond },
  { nom: 'texte-sur-fond-doux', texte: COULEURS.texte, fond: COULEURS.fondDoux },
  { nom: 'texte-secondaire', texte: COULEURS.texteSecondaire, fond: COULEURS.fond },
  { nom: 'texte-secondaire-sur-fond-doux', texte: COULEURS.texteSecondaire, fond: COULEURS.fondDoux },
  { nom: 'bouton', texte: '#FFFFFF', fond: COULEURS.bouton },
  { nom: 'marine-sur-blanc', texte: COULEURS.marine, fond: COULEURS.fond },
  { nom: 'marine-sur-fond-doux', texte: COULEURS.marine, fond: COULEURS.fondDoux },
  { nom: 'bandeau', texte: '#FFFFFF', fond: COULEURS.marine },
  { nom: 'bandeau-bouton', texte: COULEURS.marine, fond: COULEURS.fond },
  { nom: 'texte-sur-corail', texte: COULEURS.texte, fond: COULEURS.corail },
  { nom: 'texte-sur-corail-clair', texte: COULEURS.texte, fond: COULEURS.corailClair },
  { nom: 'erreur', texte: COULEURS.erreur, fond: COULEURS.fondErreur },
  { nom: 'erreur-sur-blanc', texte: COULEURS.erreur, fond: COULEURS.fond },
  ...Object.entries(COULEURS_STATUT).map(([statut, c]) => ({ nom: `statut-${statut}`, texte: c.texte, fond: c.fond })),
];

export interface PaireComposant {
  nom: string;
  avant: string;
  fond: string;
}

/** Contours et marques d'interface : 3:1 minimum (UX-23). */
export const PAIRES_COMPOSANTS: readonly PaireComposant[] = [
  { nom: 'bordure-sur-blanc', avant: COULEURS.bordure, fond: COULEURS.fond },
  { nom: 'bordure-sur-fond-doux', avant: COULEURS.bordure, fond: COULEURS.fondDoux },
  { nom: 'focus-sur-blanc', avant: COULEURS.focus, fond: COULEURS.fond },
  { nom: 'bouton-sur-blanc', avant: COULEURS.bouton, fond: COULEURS.fond },
];

export const TYPO = {
  /** Corps de texte côté client (héritier, mandant) : 18 px minimum (UX-20). */
  corpsClient: 18,
  /** Corps de texte côté équipe : 16 px minimum. */
  corpsEquipe: 16,
  interligne: 1.5,
  /** 70 caractères par ligne au plus. */
  mesureMax: '70ch',
  policeEquipe: "'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  policeTitres: "'Playfair Display', Georgia, 'Times New Roman', serif",
  /** Portails client : police système, aucune police chargée (poids de page, SYNTHESE §2). */
  policeClient: "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
} as const;

/** Arrondis du prototype de démonstration (référence visuelle) : cartes 12, boutons et champs 10, pastilles ovales. */
export const RAYONS = { carte: 12, bouton: 10, champ: 10, pastille: 999 } as const;

/** Ombres douces à base de marine foncé, comme le prototype (cartes, fenêtres). */
export const OMBRES = {
  carte: '0 1px 2px rgba(38, 42, 78, 0.06), 0 4px 16px rgba(38, 42, 78, 0.06)',
  fenetre: '0 12px 40px rgba(38, 42, 78, 0.25)',
} as const;

export const CIBLES = {
  minimale: 44,
  client: 48,
  espacement: 8,
} as const;

export const ESPACEMENTS = { xs: 4, s: 8, m: 16, l: 24, xl: 32, xxl: 48 } as const;

const enKebab = (nom: string): string => nom.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

/** Variables CSS `--maat-*` des jetons, pour les pages qui ne passent pas par les styles en ligne. */
export function jetonsCss(): string {
  const lignes = [
    ...Object.entries(COULEURS).map(([nom, valeur]) => `  --maat-${enKebab(nom)}: ${valeur};`),
    `  --maat-rayon-carte: ${RAYONS.carte}px;`,
    `  --maat-rayon-bouton: ${RAYONS.bouton}px;`,
    `  --maat-ombre-carte: ${OMBRES.carte};`,
    `  --maat-corps-client: ${TYPO.corpsClient}px;`,
    `  --maat-corps-equipe: ${TYPO.corpsEquipe}px;`,
    `  --maat-interligne: ${TYPO.interligne};`,
    `  --maat-mesure-max: ${TYPO.mesureMax};`,
    `  --maat-cible: ${CIBLES.minimale}px;`,
    `  --maat-cible-client: ${CIBLES.client}px;`,
    ...Object.entries(ESPACEMENTS).map(([nom, valeur]) => `  --maat-espace-${nom}: ${valeur}px;`),
  ];
  return `:root {\n${lignes.join('\n')}\n}\n`;
}

/** Base typographique d'un composant : taille, interligne et police selon la variante. */
export function styleBase(variante: Variante): CSSProperties {
  return {
    fontFamily: variante === 'client' ? TYPO.policeClient : TYPO.policeEquipe,
    fontSize: variante === 'client' ? TYPO.corpsClient : TYPO.corpsEquipe,
    lineHeight: TYPO.interligne,
    color: COULEURS.texte,
  };
}

/** Taille minimale d'une cible tactile : 44 px côté équipe, 48 px côté client (UX-21). */
export function styleCible(variante: Variante): CSSProperties {
  const taille = variante === 'client' ? CIBLES.client : CIBLES.minimale;
  return { minHeight: taille, minWidth: taille, boxSizing: 'border-box' };
}

/** Paragraphe de lecture : 70 caractères au plus par ligne. */
export function styleParagraphe(variante: Variante): CSSProperties {
  return { ...styleBase(variante), maxWidth: TYPO.mesureMax, margin: 0 };
}
